import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import ManagementHeader from './ManagementHeader';
import MgField from './MgField';
import PermissionTree from './PermissionTree';
import { createUser, fetchRoleGroups } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import './management.css';

const unmapStatus = (displayStatus) => {
    const statusMap = {
        '활성': 'ACTIVE',
        '비활성': 'INACTIVE',
        '잠김': 'LOCKED'
    };
    return statusMap[displayStatus] || 'ACTIVE';
};

function UserRegisterPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [roleGroups, setRoleGroups] = useState([]);

    // 권한 그룹 목록 API에서 가져오기
    useEffect(() => {
        const loadRoleGroups = async () => {
            try {
                const data = await fetchRoleGroups();
                setRoleGroups(data);
            } catch (err) {
                console.error('권한 그룹 목록 조회 실패:', err);
                alert(t('pages.management.userRegister.alertRoleGroupFail'));
            }
        };

        loadRoleGroups();
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        position: '',
        roleGroupId: '', // 기본값: 빈 값 (첫 번째 권한 그룹으로 설정됨)
        department: '',
        status: '활성'
    });

    // 권한 그룹이 로드되면 기본값 설정
    useEffect(() => {
        if (roleGroups.length > 0 && !formData.roleGroupId) {
            setFormData(prev => ({
                ...prev,
                roleGroupId: roleGroups[0].id
            }));
        }
    }, [roleGroups]);

    // 선택된 권한 그룹 가져오기
    const selectedRoleGroup = roleGroups.find(rg => rg.id === parseInt(formData.roleGroupId));

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 비밀번호 확인 검증
        if (formData.password !== formData.confirmPassword) {
            alert(t('pages.management.userRegister.alertPasswordMismatch'));
            return;
        }

        try {
            const userData = {
                email: formData.email,
                password: formData.password,
                name: formData.name,
                phone: formData.phone || null,
                department: formData.department || null,
                position: formData.position || null,
                roleGroupId: parseInt(formData.roleGroupId),
                status: unmapStatus(formData.status)
            };

            await createUser(userData);
            alert(t('pages.management.userRegister.alertSuccess'));
            navigate('/user');
        } catch (error) {
            console.error('Failed to create user:', error);
            alert(t('pages.management.userRegister.alertFail'));
        }
    };

    const L = (key) => t(`pages.management.userRegister.label.${key}`);
    const P = (key) => t(`pages.management.userRegister.placeholder.${key}`);
    const input = (name, { type = 'text', required = false, ...rest } = {}) => (
        <input type={type} name={name} className="mg-input" placeholder={P(name)} value={formData[name]} onChange={handleInputChange} required={required} {...rest} />
    );

    return (
        <MainLayout>
            <div className="mg-page">
                <ManagementHeader rootLabel={t('sidebar.user.title')} icon="manage_accounts" crumbs={[{ label: t('sidebar.user.register') }]} />

                <form className="mg-form" onSubmit={handleSubmit}>
                    {/* 기본 정보 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.userRegister.section.basic')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('name')} required>{input('name', { required: true })}</MgField>
                            <MgField label={L('email')} required>{input('email', { type: 'email', required: true, autoComplete: 'off' })}</MgField>
                            <MgField label={L('password')} required>{input('password', { type: 'password', required: true, autoComplete: 'new-password', minLength: 8 })}</MgField>
                            <MgField label={L('confirmPassword')} required>{input('confirmPassword', { type: 'password', required: true, autoComplete: 'new-password' })}</MgField>
                        </div>
                    </div>

                    {/* 조직 정보 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.userRegister.section.organization')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('phone')}>{input('phone', { type: 'tel' })}</MgField>
                            <MgField label={L('department')}>{input('department')}</MgField>
                            <MgField label={L('position')}>{input('position')}</MgField>
                            <MgField label={L('status')} required>
                                <select name="status" className="mg-select" value={formData.status} onChange={handleInputChange} required>
                                    <option value="활성">{t('pages.management.userRegister.statusOption.active')}</option>
                                    <option value="비활성">{t('pages.management.userRegister.statusOption.inactive')}</option>
                                </select>
                            </MgField>
                            <MgField label={L('roleGroup')} required>
                                <select name="roleGroupId" className="mg-select" value={formData.roleGroupId} onChange={handleInputChange} required>
                                    {roleGroups.map(rg => (
                                        <option key={rg.id} value={rg.id}>{rg.name} ({rg.code})</option>
                                    ))}
                                </select>
                            </MgField>
                        </div>
                    </div>

                    {/* 권한 (선택한 권한 그룹에 따라 정해짐, 읽기 전용) */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">
                            {t('pages.management.userRegister.section.permissions')}
                            <span className="mg-panel-note">{t('pages.management.userRegister.permissionsNote')}</span>
                        </h3>
                        <PermissionTree
                            permissions={selectedRoleGroup?.permissions}
                            etcLabel={t('pages.management.userRegister.permissionCategoryEtc')}
                            emptyLabel={t('pages.management.userRegister.permissionsEmpty')}
                        />
                    </div>

                    <div className="mg-form-actions">
                        <button type="button" className="mg-btn secondary" onClick={() => navigate('/user')}>
                            {t('pages.management.userRegister.btn.cancel')}
                        </button>
                        <button type="submit" className="mg-btn primary">
                            {t('pages.management.userRegister.btn.submit')}
                        </button>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
}

export default UserRegisterPage;
